/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ErrorTextContainer } from 'src/core/components/ErrorTextContainer';
import { ShimmerFactory } from 'src/core/components/ShimmerFactory';
import { useDocumentEventHandler } from 'src/core/hooks/useDocumentEventHandler';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { HistoryStacks } from 'src/core/types/HistoryStacks';
import { redoKey, undoKey } from 'src/core/utils/keyboardUtils';
import { CustomTextEntityLabelStore } from 'src/customText/core/stores/CustomTextEntityLabelStore';
import { ExtractionLabeler, ExtractionLabelerProps, LabelingMode } from 'src/customText/verticals/extractionLabeler/ExtractionLabeler';
import { Language } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedLanguages';
import { isDocumentSwaggerRtl, isDocumentSwaggerWordTokenized } from 'src/languageStudio/core/utils/demosUtils';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';

type DocumentViewProps = {
    documentCulture: string;
    isDocumentLoading: boolean;
    labelingMode: LabelingMode;
    onInvalidSelection?: () => void;
    isExtractionLabelerDisabled: boolean;
    labelingHistoryStacks: HistoryStacks<CustomTextEntityLabelStore[]>;
};

const { styleComponent, styleDiv } = getStylistV2('DocumentView');

const DocumentWrapper = styleDiv('DocumentWrapper', {
    flex: 1,
    position: 'relative'
});

const StyledExtractionLabeler = styleComponent<ExtractionLabelerProps>(ExtractionLabeler)('StyledExtractionLabeler', {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflowY: 'auto'
});

export const DocumentLoadingView = () => <ShimmerFactory includeHeaderShimmer={false} fullWidth shimmerCount={15} shimmerSpacing={20} />;

const messages = defineMessages({
    entityLabelErrorTitle: {
        id: 'customText.documentView.entityLabelErrorTitle',
        defaultMessage: 'There is an error with one of your entity labels'
    },
    entityLabelErrorDescription: {
        id: 'customText.documentView.entityLabelErrorDescription',
        defaultMessage: 'Please check label with: start index "{ startIndex }" and end index "{ endIndex }".'
    },
    acceptOrRejectSingleAutotagTitle: {
        id: 'customText.documentView.acceptOrRejectSingleAutotagTitle',
        defaultMessage: 'Review auto-labeled suggestions'
    },
    acceptOrRejectSingleAutotagBody: {
        id: 'customText.documentView.acceptOrRejectSingleAutotagBody',
        defaultMessage:
            'Check each auto-label to see if it makes sense. You can accept or reject a label, then retrain your model to apply. If you ignore the auto-label suggestions, no action will be taken.'
    }
});

export const DocumentView = observer((props: DocumentViewProps) => {
    const {
        isExtractionLabelerDisabled,
        labelingHistoryStacks,
        isDocumentLoading,
        labelingMode,
        documentCulture,
        onInvalidSelection
    } = props;
    const { localize } = useLocalization();
    const { rootStore, dispatcher } = useGlobalState();

    const { currentDocumentStore, gptPredictionsStore } = rootStore.currentCustomTextProjectStore;
    const gptPredictedDocumentData = gptPredictionsStore.documents.find(d => d.name === currentDocumentStore?.name);
    const entities = rootStore.currentCustomTextProjectStore.extractionStore?.entities ?? [];
    const haveAutotags = currentDocumentStore?.entityLabels.some(l => l.labelType === 'autoTagged');
    const annotations = currentDocumentStore?.entityLabels
        .map(l => ({ ...l, entity: entities.find(e => e.id === l.entityId) }))
        .concat(gptPredictedDocumentData?.entityLabels.map(l => ({ ...l, entity: entities.find(e => e.id === l.entityId) })) ?? []);

    const erroneousEntityLabel = currentDocumentStore?.entityLabels.find(e => {
        const minAllowableIndex = 0;
        const maxAllowableIndex = currentDocumentStore.text.length - 1;

        return (
            e.startCharIndex < minAllowableIndex ||
            e.endCharIndex < minAllowableIndex ||
            e.startCharIndex > maxAllowableIndex ||
            e.endCharIndex > maxAllowableIndex
        );
    });

    React.useEffect(() => () => dequeueTeachingBubble('CustomTextAcceptRejectSingleAutoTag'), []);

    React.useEffect(() => {
        if (haveAutotags) {
            createTeachingBubble(
                'CustomTextAcceptRejectSingleAutoTag',
                localize(messages.acceptOrRejectSingleAutotagBody),
                localize(messages.acceptOrRejectSingleAutotagTitle),
                `[data-autotag-index="0"]`,
                true,
                false
            );
        }
    }, [currentDocumentStore]);

    const handleCreateEntity = (entityName: string) => {
        dispatcher.dispatch('addNewExtractionEntity', {
            entityName,
            toastData: {}
        });
    };

    useDocumentEventHandler('keyup', event => {
        if (undoKey(event)) {
            event.stopPropagation();
            dispatcher.dispatch('undoExtractionLabels', {
                labelingHistoryStacks,
                toastData: {}
            });
        }

        if (redoKey(event)) {
            event.stopPropagation();
            dispatcher.dispatch('redoExtractionLabels', {
                labelingHistoryStacks,
                toastData: {}
            });
        }
    });

    const handleAddLabel: ExtractionLabelerProps['onAddLabel'] = React.useCallback(
        label => {
            dispatcher.dispatch('submitExtractionLabels', {
                labelingHistoryStacks,
                action: {
                    name: 'AddLabel',
                    label: {
                        start: label.startCharIndex,
                        end: label.endCharIndex,
                        entityId: label.entityId
                    }
                },
                toastData: {}
            });
        },
        [labelingHistoryStacks]
    );

    const handleRemoveLabel: ExtractionLabelerProps['onRemoveLabel'] = React.useCallback(
        label => {
            if (label.labelType === 'gptPredicted') {
                dispatcher.dispatch('rejectGptLabels', { labels: [label], toastData: {} });
            } else {
                dispatcher.dispatch('submitExtractionLabels', {
                    labelingHistoryStacks,
                    action: {
                        name: 'RemoveLabels',
                        labels: [{ start: label.startCharIndex, end: label.endCharIndex, entityId: label.entityId }]
                    },
                    toastData: {}
                });
            }
        },
        [labelingHistoryStacks]
    );

    const handleConfirmAutoTags: ExtractionLabelerProps['onConfirmAutoTags'] = React.useCallback(
        labels => {
            const gptPredictedLabels = labels.filter(l => l.labelType === 'gptPredicted');
            if (gptPredictedLabels.length > 0) {
                dispatcher.dispatch('submitGptLabels', { labels: gptPredictedLabels, toastData: {}, labelingHistoryStacks });
            }
            dispatcher.dispatch('submitExtractionLabels', {
                labelingHistoryStacks,
                action: {
                    name: 'ConfirmPredictions',
                    labels: labels.map(label => ({
                        start: label.startCharIndex,
                        end: label.endCharIndex,
                        entityId: label.entityId
                    }))
                },
                toastData: {}
            });
        },
        [labelingHistoryStacks]
    );

    const renderDocument = () => {
        if (isDocumentLoading || !currentDocumentStore) {
            return <DocumentLoadingView />;
        }

        if (erroneousEntityLabel) {
            return (
                <ErrorTextContainer
                    title={localize(messages.entityLabelErrorTitle)}
                    subTitle={localize(messages.entityLabelErrorDescription, {
                        entityName: erroneousEntityLabel.entityId,
                        endIndex: erroneousEntityLabel.endCharIndex,
                        startIndex: erroneousEntityLabel.startCharIndex
                    })}
                />
            );
        }

        return (
            <Stack grow>
                <DocumentWrapper>
                    <StyledExtractionLabeler
                        schema={entities}
                        mode={labelingMode}
                        labels={annotations}
                        onAddLabel={handleAddLabel}
                        text={currentDocumentStore.text}
                        onRemoveLabel={handleRemoveLabel}
                        onCreateNewEntity={handleCreateEntity}
                        onInvalidSelection={onInvalidSelection}
                        onConfirmAutoTags={handleConfirmAutoTags}
                        isExtractionLabelerDisabled={isExtractionLabelerDisabled}
                        isRtl={documentCulture ? isDocumentSwaggerRtl(documentCulture as Language['code']) : false}
                        isWordTokenized={documentCulture ? isDocumentSwaggerWordTokenized(documentCulture as Language['code']) : false}
                    />
                </DocumentWrapper>
            </Stack>
        );
    };

    return renderDocument();
});
